import { BooleanInput, Button, Container, Element, Label, SelectInput, TextInput } from '@playcanvas/pcui';

import { parseCameraMotionCsv, type CameraCsvFormat } from '../csv-camera-parser';
import { Events } from '../events';
import type { CameraMotionAxisPreset, CameraMotionImportOptions } from '../camera-motion-import';
import { localize } from './localization';
import sceneExport from './svg/export.svg';

const createSvg = (svgString: string, args = {}) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new Element({
        dom: new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement,
        ...args
    });
};

class CameraMotionImportDialog extends Container {
    show: (args?: { text?: string; filename?: string }) => Promise<CameraMotionImportOptions | null>;
    hide: () => void;
    destroy: () => void;

    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'camera-motion-import-dialog',
            class: 'settings-dialog',
            hidden: true,
            tabIndex: -1
        };

        super(args);

        // a11y
        this.dom.setAttribute('role', 'dialog');
        this.dom.setAttribute('aria-modal', 'true');

        const dialog = new Container({ id: 'dialog' });

        // header
        const headerIcon = createSvg(sceneExport, { id: 'icon' });
        const headerText = new Label({ id: 'text', text: localize('popup.camera-motion.header').toUpperCase() });
        const header = new Container({ id: 'header' });
        header.append(headerIcon);
        header.append(headerText);

        // content
        const content = new Container({ id: 'content' });

        // Preview (computed from CSV text if provided)
        const preview = new Container({ id: 'camera-motion-preview' });
        const previewSummary = new Label({ text: '' });
        const previewNote = new Label({ text: '' });
        previewSummary.dom.style.fontSize = '11px';
        previewSummary.dom.style.opacity = '0.85';
        previewNote.dom.style.fontSize = '11px';
        previewNote.dom.style.opacity = '0.75';
        previewNote.dom.style.marginTop = '2px';
        preview.append(previewSummary);
        preview.append(previewNote);

        // axis preset
        const axisLabel = new Label({ class: 'label', text: localize('popup.camera-motion.axis-preset') });
        const axisSelect = new SelectInput({
            id: 'camera-motion-axis-preset',
            class: 'select',
            defaultValue: 'zUpToYUp',
            options: [
                { v: 'zUpToYUp', t: localize('popup.camera-motion.axis-preset.zup') },
                { v: 'yUpKeep', t: localize('popup.camera-motion.axis-preset.yup') }
            ]
        });
        const axisRow = new Container({ class: 'row' });
        axisRow.append(axisLabel);
        axisRow.append(axisSelect);

        // fps override
        const fpsLabel = new Label({ class: 'label', text: localize('popup.camera-motion.fps') });
        const fpsInput = new TextInput({
            id: 'camera-motion-fps',
            class: 'text-input'
        });
        const fpsRow = new Container({ class: 'row' });
        fpsRow.append(fpsLabel);
        fpsRow.append(fpsInput);

        // extend timeline
        const extendLabel = new Label({ class: 'label', text: localize('popup.camera-motion.extend-timeline') });
        const extendBoolean = new BooleanInput({
            id: 'camera-motion-extend-timeline',
            class: 'boolean',
            value: true
        });
        const extendRow = new Container({ class: 'row' });
        extendRow.append(extendLabel);
        extendRow.append(extendBoolean);

        // yaw invert
        const invertYawLabel = new Label({ class: 'label', text: localize('popup.camera-motion.invert-yaw') });
        const invertYawBoolean = new BooleanInput({
            id: 'camera-motion-invert-yaw',
            class: 'boolean',
            value: false
        });
        const invertYawRow = new Container({ class: 'row' });
        invertYawRow.append(invertYawLabel);
        invertYawRow.append(invertYawBoolean);

        // pitch invert
        const invertPitchLabel = new Label({ class: 'label', text: localize('popup.camera-motion.invert-pitch') });
        const invertPitchBoolean = new BooleanInput({
            id: 'camera-motion-invert-pitch',
            class: 'boolean',
            value: false
        });
        const invertPitchRow = new Container({ class: 'row' });
        invertPitchRow.append(invertPitchLabel);
        invertPitchRow.append(invertPitchBoolean);

        // roll invert
        const invertRollLabel = new Label({ class: 'label', text: localize('popup.camera-motion.invert-roll') });
        const invertRollBoolean = new BooleanInput({
            id: 'camera-motion-invert-roll',
            class: 'boolean',
            value: false
        });
        const invertRollRow = new Container({ class: 'row' });
        invertRollRow.append(invertRollLabel);
        invertRollRow.append(invertRollBoolean);

        // swap pitch/roll
        const swapLabel = new Label({ class: 'label', text: localize('popup.camera-motion.swap-pitch-roll') });
        const swapBoolean = new BooleanInput({
            id: 'camera-motion-swap-pitch-roll',
            class: 'boolean',
            value: false
        });
        const swapRow = new Container({ class: 'row' });
        swapRow.append(swapLabel);
        swapRow.append(swapBoolean);

        content.append(axisRow);
        content.append(fpsRow);
        content.append(extendRow);
        content.append(invertYawRow);
        content.append(invertPitchRow);
        content.append(invertRollRow);
        content.append(swapRow);

        // Put preview at the top so users immediately see CSV metadata.
        content.dom.insertBefore(preview.dom, content.dom.firstChild);

        // footer
        const footer = new Container({ id: 'footer' });

        const cancelButton = new Button({
            id: 'camera-motion-import-cancel',
            class: 'button',
            text: localize('popup.cancel')
        });

        const importButton = new Button({
            id: 'camera-motion-import-ok',
            class: 'button',
            text: localize('popup.camera-motion.import')
        });

        footer.append(cancelButton);
        footer.append(importButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);

        this.append(dialog);

        // handlers
        let onCancel: () => void;
        let onImport: () => void;

        cancelButton.on('click', () => onCancel());
        importButton.on('click', () => onImport());

        const keydown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Escape':
                    e.preventDefault();
                    e.stopPropagation();
                    onCancel();
                    break;
                case 'Enter':
                    e.preventDefault();
                    e.stopPropagation();
                    onImport();
                    break;
                default:
                    // Prevent editor shortcuts / timeline keybinds from firing while modal is open.
                    e.stopPropagation();
                    break;
            }
        };

        const reset = () => {
            axisSelect.value = 'zUpToYUp';
            extendBoolean.value = true;
            invertYawBoolean.value = false;
            invertPitchBoolean.value = false;
            invertRollBoolean.value = false;
            swapBoolean.value = false;

            // Leave FPS blank by default so importer can use current timeline frameRate.
            fpsInput.value = '';

            const timelineFps = events.invoke('timeline.frameRate');
            const hint = (typeof timelineFps === 'number' && Number.isFinite(timelineFps) && timelineFps > 0) ? String(timelineFps) : '30';

            // Best-effort placeholder support (PCUI doesn't expose this consistently across versions).
            try {
                const inputEl = (fpsInput as any).input as HTMLInputElement | undefined;
                if (inputEl) {
                    inputEl.placeholder = `${localize('popup.camera-motion.fps.placeholder')} (${hint})`;
                }
            } catch (err) {
                // ignore
            }
        };

        // Preview state
        let csvText: string | null = null;
        let csvFilename: string | null = null;
        let parsedFormat: CameraCsvFormat | null = null;
        let parsedDurationSec = 0;
        let parsedSampleCount = 0;
        let parsedWarnings: string[] = [];
        let parsedOk = false;

        const setEulerRowsHidden = (hidden: boolean) => {
            invertYawRow.hidden = hidden;
            invertPitchRow.hidden = hidden;
            invertRollRow.hidden = hidden;
            swapRow.hidden = hidden;

            if (hidden) {
                invertYawBoolean.value = false;
                invertPitchBoolean.value = false;
                invertRollBoolean.value = false;
                swapBoolean.value = false;
            }
        };

        const parsePreview = () => {
            parsedOk = false;
            parsedWarnings = [];
            parsedDurationSec = 0;
            parsedSampleCount = 0;
            parsedFormat = null;

            if (!csvText) {
                return;
            }

            const parsed = parseCameraMotionCsv(csvText, { zeroTimeToFirstSample: true });
            parsedWarnings = parsed.warnings ?? [];
            parsedSampleCount = parsed.samples?.length ?? 0;
            parsedFormat = parsed.format;

            if (parsedSampleCount > 0) {
                parsedDurationSec = parsed.samples[parsedSampleCount - 1].tSec;
                parsedOk = true;
            }
        };

        const getEffectiveFps = (): number => {
            const rawFps = String(fpsInput.value ?? '').trim();
            if (rawFps !== '') {
                const parsed = Number(rawFps);
                if (Number.isFinite(parsed) && parsed > 0) return parsed;
            }

            const timelineFps = events.invoke('timeline.frameRate');
            if (typeof timelineFps === 'number' && Number.isFinite(timelineFps) && timelineFps > 0) return timelineFps;
            return 30;
        };

        const getFramesRequired = (durationSec: number, fps: number) => {
            return Math.max(1, Math.ceil(durationSec * fps) + 1);
        };

        const formatText = (format: CameraCsvFormat | null) => {
            if (format === 'posTarget') return localize('popup.camera-motion.format.postarget');
            if (format === 'cogEuler') return localize('popup.camera-motion.format.cogeuler');
            return localize('popup.camera-motion.format.unknown');
        };

        const updatePreview = () => {
            // Default: blank preview if no CSV text was provided.
            if (!csvText) {
                previewSummary.text = '';
                previewNote.text = '';
                setEulerRowsHidden(false);
                importButton.disabled = false;
                return;
            }

            setEulerRowsHidden(parsedFormat === 'posTarget');

            if (!parsedOk) {
                importButton.disabled = true;

                const reason = parsedWarnings[0] ?? localize('popup.camera-motion.preview.parse-failed');
                const formatLabel = formatText(parsedFormat);
                const filePart = csvFilename ? `${csvFilename} — ` : '';

                previewSummary.text = `${filePart}${reason}`;
                previewNote.text = `${localize('popup.camera-motion.preview.format')}: ${formatLabel}`;
                previewSummary.dom.style.color = '#f88';
                return;
            }

            importButton.disabled = false;
            previewSummary.dom.style.color = '';

            const fps = getEffectiveFps();
            const framesRequired = getFramesRequired(parsedDurationSec, fps);
            const formatLabel = formatText(parsedFormat);
            const filePart = csvFilename ? `${csvFilename} — ` : '';

            previewSummary.text = `${filePart}${parsedSampleCount} ${localize('popup.camera-motion.preview.samples')} • ${parsedDurationSec.toFixed(2)}s • ${localize('popup.camera-motion.preview.format')}: ${formatLabel}`;

            const currentFrames = events.invoke('timeline.frames');
            const extend = !!extendBoolean.value;
            const currentFramesNum = (typeof currentFrames === 'number' && Number.isFinite(currentFrames)) ? currentFrames : null;

            if (extend && currentFramesNum !== null && framesRequired > currentFramesNum) {
                previewNote.text = `${localize('popup.camera-motion.preview.timeline-extends')} ${framesRequired} ${localize('popup.camera-motion.preview.frames')} @ ${fps}${localize('popup.camera-motion.preview.fps-suffix')}`;
            } else if (!extend && currentFramesNum !== null && framesRequired > currentFramesNum) {
                previewNote.text = `${localize('popup.camera-motion.preview.timeline-exceeds')} ${currentFramesNum} ${localize('popup.camera-motion.preview.frames')}.`;
            } else {
                previewNote.text = `${framesRequired} ${localize('popup.camera-motion.preview.frames')} @ ${fps}${localize('popup.camera-motion.preview.fps-suffix')}`;
            }
        };

        // Keep preview reactive to option changes.
        fpsInput.on('change', () => updatePreview());
        extendBoolean.on('change', () => updatePreview());

        this.show = (args?: { text?: string; filename?: string }) => {
            reset();

            csvText = typeof args?.text === 'string' ? args.text : null;
            csvFilename = typeof args?.filename === 'string' ? args.filename : null;
            parsePreview();
            updatePreview();

            this.hidden = false;
            this.dom.addEventListener('keydown', keydown);
            this.dom.focus();

            return new Promise<CameraMotionImportOptions | null>((resolve) => {
                onCancel = () => resolve(null);

                onImport = async () => {
                    const axisPreset = axisSelect.value as CameraMotionAxisPreset;

                    const rawFps = String(fpsInput.value ?? '').trim();
                    let fps: number | undefined;
                    if (rawFps !== '') {
                        const parsed = Number(rawFps);
                        if (!Number.isFinite(parsed) || parsed <= 0) {
                            await events.invoke('showPopup', {
                                type: 'error',
                                header: localize('popup.error'),
                                message: localize('popup.camera-motion.fps.invalid')
                            });
                            return;
                        }
                        fps = parsed;
                    }

                    const eulerOverrides = {
                        invertYaw: !!invertYawBoolean.value,
                        invertPitch: !!invertPitchBoolean.value,
                        invertRoll: !!invertRollBoolean.value,
                        swapPitchRoll: !!swapBoolean.value
                    };

                    resolve({
                        axisPreset,
                        eulerOverrides,
                        fps,
                        extendTimeline: !!extendBoolean.value
                    });
                };
            }).finally(() => {
                this.dom.removeEventListener('keydown', keydown);
                this.hide();
            });
        };

        this.hide = () => {
            this.hidden = true;
        };

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { CameraMotionImportDialog };
